import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, useServer } from '../contexts/ServerContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { Cat, Clock9, RefreshCw, Monitor } from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import { Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { steamId64ToV2, proxyImageSrc } from '../utils/helpers';
import CommentsTab from './CommentsTab';
import PlayerNote from './PlayerNote';
import PlayerTags from './PlayerTags';
import ScreengrabLightbox from './ScreengrabLightbox';

Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

function steamIdTo64(steamID) {
  if (!steamID) return '';
  if (/^\d{17}$/.test(steamID)) return steamID;
  if (!steamID.startsWith('STEAM_')) return '';
  const parts = steamID.split(':');
  if (parts.length !== 3) return '';
  const Y = parseInt(parts[1], 10);
  const Z = parseInt(parts[2], 10);
  const base = '76561197960265728';
  const offset = (Z * 2 + Y).toString();
  function addStrings(a, b) {
    let res = '';
    let carry = 0;
    a = a.split('').reverse();
    b = b.split('').reverse();
    let len = Math.max(a.length, b.length);
    for (let i = 0; i < len; i++) {
      let digitA = parseInt(a[i] || '0', 10);
      let digitB = parseInt(b[i] || '0', 10);
      let sum = digitA + digitB + carry;
      res = (sum % 10) + res;
      carry = Math.floor(sum / 10);
    }
    if (carry) res = carry + res;
    return res;
  }
  return addStrings(base, offset);
}

function PlayerDetails() {
  const { steamId } = useParams();
  const { server } = useServer();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatar, setAvatar] = useState(null);
  const [steamId64, setSteamId64] = useState(null);
  const { user } = useAuth();

  const [punishments, setPunishments] = useState([]);
  const [punLoading, setPunLoading] = useState(true);
  const [punCurrentPage, setPunCurrentPage] = useState(1);
  const [punTotalPages, setPunTotalPages] = useState(1);
  const punPageSize = 20;
  const [punPageInput, setPunPageInput] = useState(1);
  const [pvpLogs, setPvpLogs] = useState([]);
  const [pvpLoading, setPvpLoading] = useState(true);
  const [pvpAvatars, setPvpAvatars] = useState({});
  const [pvpNames, setPvpNames] = useState({});
  const [commands, setCommands] = useState([]);
  const [cmdPage, setCmdPage] = useState(1);
  const [cmdTotalPages, setCmdTotalPages] = useState(1);
  const cmdPageSize = 25;
  const [cmdPageInput, setCmdPageInput] = useState(1);
  const [activeTab, setActiveTab] = useState('main');
  const [adminStats, setAdminStats] = useState(null);
  const [adminStatsLoading, setAdminStatsLoading] = useState(false);
  const [reporterAvatars, setReporterAvatars] = useState({});
  const [adminStatsPage, setAdminStatsPage] = useState(1);
  const [adminStatsTotalPages, setAdminStatsTotalPages] = useState(1);
  const [pvpSteamIds, setPvpSteamIds] = useState({});
  const [onlineStats, setOnlineStats] = useState(null);
  const [onlineStatsLoading, setOnlineStatsLoading] = useState(false);
  const [onlineStatsMonth, setOnlineStatsMonth] = useState((new Date().getMonth() + 1));
  const [onlineStatsYear, setOnlineStatsYear] = useState((new Date().getFullYear()));
  const [reportNames, setReportNames] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [bgUrlInput, setBgUrlInput] = useState('');
  const [bgSaving, setBgSaving] = useState(false);
  const [mainInfoOpen, setMainInfoOpen] = useState(true);
  const [screengrabHistory, setScreengrabHistory] = useState([]);
  const [screengrabLoading, setScreengrabLoading] = useState(false);
  const [screengrabPage, setScreengrabPage] = useState(1);
  const [screengrabTotalPages, setScreengrabTotalPages] = useState(1);
  const [lightboxGrab, setLightboxGrab] = useState(null);
  const screengrabLimit = 9;
  const [jobs, setJobs] = useState(null);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsSearch, setJobsSearch] = useState('');
  const [jobsCategory, setJobsCategory] = useState('');
  const [showJobs, setShowJobs] = useState(false);
  const [confirmJob, setConfirmJob] = useState(null); // {id, job} или null

  useEffect(() => {
    const timeout = setTimeout(() => {
      const id64 = steamIdTo64(steamId);
      setSteamId64(id64);
      fetchPlayer();
      fetchAvatar(id64);
    }, 400);
  }, [steamId, server]);

  // Автоматическое обновление данных при фокусе на странице
  useEffect(() => {
    const handleFocus = () => {
      if (steamId && server) {
        fetchPlayer();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [steamId, server]);

  // Слушатель обновления ранга игрока
  useEffect(() => {
    const handlePlayerRankUpdate = (event) => {
      const { steamId: updatedSteamId, faction, rank } = event.detail;
      if (updatedSteamId === steamId && player) {
        // Обновляем данные игрока локально
        setPlayer(prevPlayer => ({
          ...prevPlayer,
          [faction === 'slayer' ? 'slayerRank' : 'demonRank']: rank
        }));
      }
    };

    window.addEventListener('playerRankUpdated', handlePlayerRankUpdate);
    return () => window.removeEventListener('playerRankUpdated', handlePlayerRankUpdate);
  }, [steamId, player]);

  useEffect(() => {
    if (steamId && server) fetchPunishments();
  }, [steamId, server, punCurrentPage]);

  useEffect(() => {
    if (pvpLogs.length > 0) {
      const ids = Array.from(new Set([
        ...pvpLogs.map(l => l.instigator),
        ...pvpLogs.map(l => l.victim)
      ].filter(Boolean)));
      ids.forEach(async (id) => {
        if (!pvpAvatars[id]) {
          try {
            const resp = await api.get(`/api/steam/avatar/${id}`);
            setPvpAvatars(prev => ({ ...prev, [id]: resp?.data?.avatar }));
          } catch {
            setPvpAvatars(prev => ({ ...prev, [id]: null }));
          }
        }
        if (!pvpNames[id] || !pvpSteamIds[id]) {
          try {
            let steamId = id;
            if (/^\d{17}$/.test(id)) {
              steamId = steamId64ToV2(id);
            }
            const resp = await api.get(`/api/players/${steamId}`);
            setPvpNames(prev => ({ ...prev, [id]: resp?.data?.displayName || resp?.data?.name || id }));
            setPvpSteamIds(prev => ({ ...prev, [id]: resp?.data?.steamId || id }));
          } catch {
            setPvpNames(prev => ({ ...prev, [id]: id }));
            setPvpSteamIds(prev => ({ ...prev, [id]: id }));
          }
        }
      });
    }
  }, [pvpLogs, server]);

  useEffect(() => { setPunPageInput(punCurrentPage); }, [punCurrentPage]);

  useEffect(() => {
    if (steamId64 && server) fetchPvpLogs();
  }, [steamId64, server]);

  useEffect(() => { setCmdPageInput(cmdPage); }, [cmdPage]);

  useEffect(() => {
    if (steamId64 && server && activeTab === 'admin') fetchAdminStats();
  }, [activeTab, steamId64, server, adminStatsPage]);

  useEffect(() => {
    if (steamId64 && server && activeTab === 'main') fetchCommands();
  }, [steamId, server, cmdPage, steamId64, activeTab]);

  useEffect(() => {
    if (!steamId || !server) return;
    setOnlineStatsLoading(true);
    api.get(`/api/players/${steamId}/online-stats?server=${server}`, {
      params: {
        year: onlineStatsYear,
        month: onlineStatsMonth
        // server будет добавлен interceptor'ом
      }
    })
      .then(res => setOnlineStats(res.data.days))
      .catch(() => setOnlineStats(null))
      .finally(() => setOnlineStatsLoading(false));
  }, [steamId, onlineStatsMonth, onlineStatsYear, server]);

  useEffect(() => {
    if (!adminStats || !adminStats.reports) return;
    const ids = Array.from(new Set([
      ...adminStats.reports.map(r => r.reporter),
      ...adminStats.reports.map(r => r.reported)
    ].filter(Boolean)));
    ids.forEach(id => {
      if (reportNames[id]) return;
      let steamId = id;
      if (/^\d{17}$/.test(id)) {
        steamId = steamId64ToV2(id);
      }
      api.get(`/api/players/${steamId}`)
        .then(resp => {
          setReportNames(prev => ({ ...prev, [id]: resp?.data?.displayName || resp?.data?.name || id }));
        })
        .catch(() => {
          setReportNames(prev => ({ ...prev, [id]: id }));
        });
    });
  }, [adminStats, server]);

  useEffect(() => {
    if (player && player.backgroundUrl) setBgUrlInput(player.backgroundUrl);
    else setBgUrlInput('');
  }, [player]);

  const saveBgUrl = async () => {
    setBgSaving(true);
    try {
      await api.post(`/api/players/${player.steamId}/background`, { backgroundUrl: bgUrlInput });
      toast.success('Фон обновлён');
      fetchPlayer();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Ошибка при сохранении');
    } finally {
      setBgSaving(false);
    }
  };

  const fetchPlayer = async () => {
    try {
      const res = await api.get(`/api/players/${steamId}`, { withCredentials: true });
      setPlayer(res?.data || null);
    } catch {
      setPlayer(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshPlayerData = async () => {
    setRefreshing(true);
    try {
      await fetchPlayer();
      toast.success('Данные игрока обновлены');
    } catch (error) {
      toast.error('Ошибка при обновлении данных');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchAvatar = async (id64) => {
    if (!id64) return;
    try {
      const response = await api.get(`/api/steam/avatar/${id64}`);
      setAvatar(response?.data?.avatar || null);
    } catch {
      setAvatar(null);
    }
  };

  const fetchPunishments = async () => {
    setPunLoading(true);
    try {
      const res = await api.get(`/api/punishments?search=${steamId}&page=${punCurrentPage}&limit=${punPageSize}`);
      const filtered = (res?.data?.punishments || []).filter(
        l => l.steamID === steamId || l.targetSteamID === steamId
      );
      setPunishments(filtered);
      setPunTotalPages(res?.data?.totalPages || 1);
    } catch {
      setPunishments([]);
      setPunTotalPages(1);
    } finally {
      setPunLoading(false);
    }
  };

  const fetchPvpLogs = async () => {
    setPvpLoading(true);
    try {
      let accountId = null;
      if (steamId64 && /^\d{17}$/.test(steamId64)) {
        accountId = (window.BigInt(steamId64) - 76561197960265728n).toString();
      }
      const res = await api.get(`/api/pvp-logs?${accountId ? `&steamId64=${accountId}` : ''}`);
      setPvpLogs(res?.data?.logs || []);
    } catch {
      setPvpLogs([]);
    } finally {
      setPvpLoading(false);
    }
  };

  const fetchCommands = async () => {
    try {
      let url = `/api/commands/history?limit=${cmdPageSize}&page=${cmdPage}&steamId=${steamId}`;
      if (steamId64 && steamId64 !== 'null' && steamId64 !== 'undefined') {
        url += `&steamId64=${steamId64}`;
      }
      const res = await api.get(url);
      setCommands(res?.data?.commands || []);
      setCmdTotalPages(res?.data?.totalPages || 1);
    } catch {
      setCommands([]);
      setCmdTotalPages(1);
    }
  };

  const fetchAdminStats = async () => {
    setAdminStatsLoading(true);
    try {
      const res = await api.get(`/api/admin/performance?steamid=${steamId64}&page=${adminStatsPage}&limit=50`);
      if (res && res.data) {
        setAdminStats(res.data);
        setAdminStatsTotalPages(res.data.pagination?.totalPages || 1);
      } else {
        setAdminStats({});
        setAdminStatsTotalPages(1);
      }
      const ids = Array.from(new Set((res?.data?.reports || []).map(r => r.reporter)));
      if (ids.length > 0) {
        const avatars = {};
        await Promise.all(ids.map(async (id) => {
          try {
            const resp = await api.get(`/api/players/avatar/${id}`);
            avatars[id] = resp?.data?.avatar;
          } catch { avatars[id] = null; }
        }));
        setReporterAvatars(avatars);
      }
    } catch {
      setAdminStats(null);
      setAdminStatsTotalPages(1);
    } finally {
      setAdminStatsLoading(false);
    }
  };

  useEffect(() => {
    if (!steamId) return;
    setScreengrabLoading(true);
    api.get(`/api/admin/screengrab/history?steamId=${steamId}&limit=${screengrabLimit}&page=${screengrabPage}`)
      .then(res => {
        setScreengrabHistory(res?.data?.history || []);
        setScreengrabTotalPages(res?.data?.totalPages || 1);
      })
      .catch(() => {
        setScreengrabHistory([]);
        setScreengrabTotalPages(1);
      })
      .finally(() => setScreengrabLoading(false));
  }, [steamId, screengrabPage]);

  // При заходе на вкладку "Вайтлист" сначала пробуем получить кэш, если нет - инициируем загрузку
  useEffect(() => {
    if (activeTab === 'whitelist') {
      fetchJobsCache();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  const fetchJobsCache = async () => {
    setJobsLoading(true);
    try {
      const resp = await api.get('/api/admin/jobs-cache');
      if (resp && resp.data && typeof resp.data === 'object') {
        setJobs(resp.data);
        setShowJobs(true);
      } else {
        setJobs(null);
        setShowJobs(false);
        // Если кэша нет и у пользователя есть права - инициируем загрузку
        if (canRequestJobs) {
          fetchJobs();
        }
      }
    } catch (error) {
      setJobs(null);
      setShowJobs(false);
      // Если кэша нет и у пользователя есть права - инициируем загрузку
      if (canRequestJobs) {
        fetchJobs();
      }
    } finally {
      setJobsLoading(false);
    }
  };

  // Получить профессии (только для пользователей с правами)
  const fetchJobs = async () => {
    setJobsLoading(true);
    try {
      const resp = await api.post('/api/admin/action', {
        action: 'requestJobs',
        target: player.steamId,
        reason: '',
        duration: 0
      }, { withCredentials: true });
      if (resp && resp.data && typeof resp.data === 'object') {
        setJobs(resp.data);
        setShowJobs(true);
      } else {
        toast.error('Некорректный ответ сервера');
      }
    } catch (e) {
      toast.error('Ошибка при запросе профессий');
    } finally {
      setJobsLoading(false);
    }
  };

  // Фильтрация профессий
  const filteredJobs = jobs ? Object.entries(jobs)
    .filter(([id, job]) => {
      const nameMatch = (job.name || '').toLowerCase().includes((jobsSearch || '').toLowerCase());
      const catMatch = jobsCategory ? job.category === jobsCategory : true;
      return nameMatch && catMatch;
    })
    .sort((a, b) => Number(a[0]) - Number(b[0])) : [];

  // Категории для dropdown
  const jobCategories = jobs ? Array.from(new Set(Object.values(jobs).map(j => j.category).filter(Boolean))) : [];

  // Подтверждение и выдача профы
  const handleSetWhitelist = (jobId) => {
    const job = jobs[jobId];
    setConfirmJob({ id: jobId, job });
  };
  const confirmSetWhitelist = () => {
    if (!confirmJob) return;
    api.post('/api/admin/action', {
      action: `SetWhiteListID@${player.steamId}@${Number(confirmJob.id)+1}`,
      target: player.steamId,
      reason: '',
      duration: 0
    }, { withCredentials: true })
      .then(() => toast.success('Профессия выдана!'))
      .catch(() => toast.error('Ошибка при выдаче профы'));
    setConfirmJob(null);
  };
  const cancelSetWhitelist = () => setConfirmJob(null);

  // При переключении на вкладку "Вайтлист" показывать кэш, если есть
  useEffect(() => {
    if (activeTab === 'whitelist' && jobs && !showJobs) {
      setShowJobs(true);
    }
  }, [activeTab, jobs, showJobs]);

  const canRequestJobs = user?.rank === 'superadmin' || user?.rank === 'yufuadmin' || user?.rank === 'curator' || user?.rank === 'seniorstaff';

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;
  }
  if (!player) {
    return <div className="text-gmod-white">Игрок не найден</div>;
  }
  // if ((player.rank === 'superadmin' || player.rank === 'yufuadmin') && user && user.steamId !== steamIdTo64(player.steamId)) {
  //   return <div className="text-gmod-white text-center text-xl mt-16">Профиль этого пользователя закрыт</div>;
  // }

  // --- Новый layout профиля ---

  return (
    <div className="relative w-full max-w-5xl mx-auto px-2 md:px-0">
      {/* Новый фон профиля */}
      {player.backgroundUrl && (
        <img
          src={proxyImageSrc(player.backgroundUrl)}
          alt="profile-bg"
          className="absolute left-0 top-0 w-full h-[340px] object-cover z-0 rounded-2xl shadow-xl"
          style={{ filter: 'brightness(0.45) blur(1px)', pointerEvents: 'none', maxWidth: 'none' }}
        />
      )}
      <div className="relative z-10 flex flex-col items-center pt-10 pb-6">
        {/* Поле для backgroundUrl и кнопка — в правый верхний угол */}
        {user && steamId64ToV2(user.steamId) === player.steamId && (
          <div className="absolute top-2 right-2 z-20 flex gap-2 items-center">
            <input
              type="text"
              className="bg-gmod-lighter border border-gmod-lighterer rounded px-2 py-1 text-gmod-white w-64"
              value={bgUrlInput}
              onChange={e => setBgUrlInput(e.target.value)}
              placeholder="Ссылка на картинку (.png/.jpg)"
              maxLength={300}
              disabled={bgSaving}
            />
            <button
              onClick={saveBgUrl}
              className="px-2 py-1 rounded bg-theme-primary text-gmod-white font-semibold hover:bg-theme-primary-hover transition-colors w-max text-sm"
              disabled={bgSaving}
            >
              {bgSaving ? '...' : 'Сохранить'}
            </button>
          </div>
        )}
        {/* Аватар и имя */}
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <img
              src={avatar || player.avatar || '/default-avatar.png'}
              alt="avatar"
              className={`w-36 h-36 rounded-full border-4 shadow-2xl ${player.isOnline ? 'border-green-500' : 'border-theme-primary'} bg-gmod-dark/80`}
              style={{ boxShadow: '0 0 0 8px rgba(0,0,0,0.25)' }}
            />
            <span className={`absolute bottom-2 right-2 w-5 h-5 border-2 border-white rounded-full ${player.isOnline ? 'bg-green-500' : 'bg-red-500'}`}></span>
          </div>
          <div className="flex flex-col items-center">
            <span
              className="text-3xl font-extrabold text-gmod-white drop-shadow-lg cursor-pointer hover:text-theme-primary transition-colors"
              onClick={() => window.open(`https://steamcommunity.com/profiles/${steamId64}`, '_blank')}
            >
              {player.displayName}
            </span>
            <span
              className="text-base text-theme-primary font-mono opacity-80 cursor-pointer hover:text-theme-primary"
              onClick={() => {navigator.clipboard.writeText(player.steamId); toast.success('SteamID скопирован!')}}
            >
              {player.steamId}
            </span>
            {/* Отображение профы, если jobsCache и professionID есть */}
            {jobs && player.professionID && jobs[player.professionID] && (
              <span
                className="mt-2 px-3 py-1 rounded-full text-lg font-bold border border-gmod-lighterer shadow"
                style={{
                  background: '#181825',
                  color: jobs[player.professionID].color && jobs[player.professionID].color.r != null ? `rgb(${jobs[player.professionID].color.r},${jobs[player.professionID].color.g},${jobs[player.professionID].color.b})` : '#fff',
                  borderColor: jobs[player.professionID].color && jobs[player.professionID].color.r != null ? `rgba(${jobs[player.professionID].color.r},${jobs[player.professionID].color.g},${jobs[player.professionID].color.b},0.4)` : '#a1a1aa'
                }}
              >
                {jobs[player.professionID].name || 'Профессия'}
              </span>
            )}
          </div>
        </div>
        {/* Основные статы */}
        <div className="flex flex-wrap justify-center gap-8 mt-32 w-full max-w-3xl">
          <div className="flex flex-col items-center bg-gmod-lighter/80 rounded-2xl px-8 py-4 min-w-[120px] shadow">
            <span className="text-gmod-white text-lg font-semibold">Последний заход</span>
            <span className="text-theme-primary text-2xl font-bold mt-1">{player.lastSeen ? new Date(player.lastSeen).toLocaleString('ru-RU') : '-'}</span>
          </div>
          <div className="flex flex-col items-center bg-gmod-lighter/80 rounded-2xl px-8 py-4 min-w-[120px] shadow">
            <span className="text-gmod-white text-lg font-semibold">Время в игре</span>
            <span className="text-theme-primary text-2xl font-bold mt-1">{Math.floor(player.playTime/60)}ч {player.playTime%60}м</span>
          </div>
          {player.rank && (
            <div className="flex flex-col items-center bg-gmod-lighter/80 rounded-2xl px-8 py-4 min-w-[120px] shadow">
              <span className="text-gmod-white text-lg font-semibold">Ранг</span>
              <span className="text-theme-primary text-2xl font-bold mt-1">{player.rank}</span>
            </div>
          )}
        </div>
        {/* Ранги */}
        {(player.slayerRank || player.demonRank) && (
          <div className="flex flex-col gap-2 mt-4 items-center">
            <div className="flex gap-4">
              {player.slayerRank && (
                <div className="bg-blue-600 text-white text-xs px-3 py-1 rounded font-semibold shadow">Slayer: {player.slayerRank}</div>
              )}
              {player.demonRank && (
                <div className="bg-red-600 text-white text-xs px-3 py-1 rounded font-semibold shadow">Demon: {player.demonRank}</div>
              )}
            </div>
          </div>
        )}
        {(true) && (
          <div className="flex gap-2 mt-8">
            <button
              className={`px-4 py-1 rounded-t-lg font-semibold text-base transition-colors ${activeTab === 'main' ? 'bg-theme-primary text-white' : 'bg-gmod-lighter text-theme-primary hover:bg-theme-primary/30'}`}
              onClick={() => setActiveTab('main')}
            >Профиль</button>
            <button
              className={`px-4 py-1 rounded-t-lg font-semibold text-base transition-colors ${activeTab === 'admin' ? 'bg-theme-primary text-white' : 'bg-gmod-lighter text-theme-primary hover:bg-theme-primary/30'}`}
              onClick={() => setActiveTab('admin')}
            >Админ-стата</button>
            <button
              className={`px-4 py-1 rounded-t-lg font-semibold text-base transition-colors ${activeTab === 'whitelist' ? 'bg-theme-primary text-white' : 'bg-gmod-lighter text-theme-primary hover:bg-theme-primary/30'}`}
              onClick={() => setActiveTab('whitelist')}
            >Вайтлист</button>
          </div>
        )}
      </div>
      {/* Контент вкладок */}
      {activeTab === 'main' && (
        <>
          {/* Блоки: график и личная заметка */}
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 bg-gmod-lighter/80 rounded-2xl p-6 shadow-lg border border-gmod-lighterer min-w-[320px]">
              <div className="text-lg font-semibold text-theme-primary mb-2">Онлайн за месяц</div>
              {onlineStatsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary"></div>
                </div>
              ) : onlineStats ? (
                <>
                  <div style={{ position: 'relative', height: 250, width: '100%' }}>
                    <Bar
                      data={{
                        labels: Array.from({length: 31}, (_, i) => `${i+1}`),
                        datasets: Array.from({length: 24}, (_, hour) => ({
                          label: `${hour}:00`,
                          data: onlineStats.map(day => day[hour] || 0),
                          backgroundColor: `rgba(220,37,90,${0.15 + hour/40})`,
                          stack: 'online',
                          barPercentage: 1.0,
                          categoryPercentage: 1.0
                        }))
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            callbacks: {
                              label: ctx => `${ctx.dataset.label}: ${ctx.parsed.y} мин.`
                            }
                          }
                        },
                        scales: {
                          x: { stacked: true, title: { display: true, text: 'День месяца' } },
                          y: { stacked: true, title: { display: true, text: 'Минут онлайн' }, beginAtZero: true }
                        }
                      }}
                      height={250}
                    />
                  </div>
                  {/* Онлайн за неделю */}
                  {Array.isArray(onlineStats) && onlineStats.length > 0 && (
                    (() => {
                      const now = new Date();
                      const today = now.getDate(); // 1-31
                      let totalMinutes = 0;
                      for (let i = 0; i < 7; i++) {
                        const dayIdx = today - 1 - i;
                        if (dayIdx >= 0 && Array.isArray(onlineStats[dayIdx])) {
                          totalMinutes += onlineStats[dayIdx].reduce((a, b) => a + (b || 0), 0);
                        }
                      }
                      const hours = (totalMinutes / 60).toFixed(1);
                      return (
                        <div className="mt-4 text-base text-gmod-white font-semibold">
                          Онлайн за неделю: {hours} ч
                        </div>
                      );
                    })()
                  )}
                </>
              ) : (
                <div className="text-gmod-white opacity-60">Нет данных</div>
              )}
            </div>
            <div className="flex-1 min-w-[320px] flex flex-col gap-6">
              <PlayerNote steamId={player.steamId} />
              <PlayerTags steamId={player.steamId} />
            </div>
          </div>
          {/* Блок комментариев */}
          <div className="mt-6">
            <CommentsTab steamId={player.steamId} isOwnPage={user && user.steamId === player.steamId} />
          </div>
        </>
      )}
      {activeTab === 'admin' && (
        <div className="mt-8 bg-gmod-dark rounded-2xl border border-gmod-lighterer p-6 shadow-lg">
          {adminStatsLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary"></div>
            </div>
          ) : !adminStats ? (
            <div className="text-gmod-white opacity-60">Нет данных по админ-стате</div>
          ) : (
            <>
              <div className="text-2xl font-bold text-theme-primary mb-4">Админ-стата</div>
              <div className="flex flex-wrap gap-6 mb-6">
                <div className="bg-gmod-lighter/80 rounded-xl px-6 py-4 min-w-[180px] flex flex-col items-center">
                  <span className="text-gmod-white text-lg font-semibold">Закрыл репортов</span>
                  <span className="text-theme-primary text-2xl font-bold mt-1">{adminStats?.performance?.closed ?? 0}</span>
                </div>
                <div className="bg-gmod-lighter/80 rounded-xl px-6 py-4 min-w-[180px] flex flex-col items-center">
                  <span className="text-gmod-white text-lg font-semibold">Взял репортов</span>
                  <span className="text-theme-primary text-2xl font-bold mt-1">{adminStats?.performance?.claimed ?? 0}</span>
                </div>
                <div className="bg-gmod-lighter/80 rounded-xl px-6 py-4 min-w-[180px] flex flex-col items-center">
                  <span className="text-gmod-white text-lg font-semibold">Время в репортах</span>
                  <span className="text-theme-primary text-2xl font-bold mt-1">{Math.round((adminStats?.performance?.timespent ?? 0) / 60)} ч</span>
                </div>
                <div className="bg-gmod-lighter/80 rounded-xl px-6 py-4 min-w-[180px] flex flex-col items-center">
                  <span className="text-gmod-white text-lg font-semibold">Рейтинг</span>
                  <span className="text-theme-primary text-2xl font-bold mt-1">
                    {adminStats?.rating && typeof adminStats.rating.total === 'number' && typeof adminStats.rating.num === 'number' && adminStats.rating.num > 0
                      ? (adminStats.rating.total / adminStats.rating.num).toFixed(2)
                      : '-'}
                  </span>
                </div>
              </div>
              {/* История репортов */}
              <div className="text-lg font-semibold text-theme-primary mb-2">История репортов</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-gmod-white opacity-80">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-center">ID</th>
                      <th className="px-4 py-2 text-center">Репортёр</th>
                      <th className="px-4 py-2 text-center">Жалоба</th>
                      <th className="px-4 py-2 text-center">Оценка</th>
                      <th className="px-4 py-2 text-center">Комментарий</th>
                      <th className="px-4 py-2 text-center">Дата</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminStats?.reports?.map((r, i) => (
                      <tr key={r.id || i} className="border-b border-gmod-lighterer">
                        <td className="px-4 py-2 text-center">{r.id}</td>
                        <td className="px-4 py-2 text-center">{reportNames[r.reporter] || r.reporter}</td>
                        <td className="px-4 py-2 text-center">{r.reason || r.complaint || '-'}</td>
                        <td className="px-4 py-2 text-center">{r.rating ?? '-'}</td>
                        <td className="px-4 py-2 text-center">{r.comment || '-'}</td>
                        <td className="px-4 py-2 text-center">{r.date ? new Date(r.date * 1000).toLocaleString('ru-RU') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Пагинация для истории репортов */}
              {adminStatsTotalPages > 1 && (
                <div className="flex justify-center space-x-2 mt-4 items-center">
                  <button
                    onClick={() => setAdminStatsPage(Math.max(1, adminStatsPage - 1))}
                    disabled={adminStatsPage === 1}
                    className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer"
                  >
                    Назад
                  </button>
                  <span className="px-2 text-gmod-white">Страница {adminStatsPage} из {adminStatsTotalPages}</span>
                  <button
                    onClick={() => setAdminStatsPage(Math.min(adminStatsTotalPages, adminStatsPage + 1))}
                    disabled={adminStatsPage === adminStatsTotalPages}
                    className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer"
                  >
                    Вперёд
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {activeTab === 'whitelist' && (
        <div className="mt-8 bg-gmod-dark rounded-2xl border border-gmod-lighterer p-6 shadow-lg min-h-[300px]">
          {jobs ? (
            <>
              <div className="flex flex-col md:flex-row gap-4 mb-4 items-center">
                <input
                  type="text"
                  value={jobsSearch}
                  onChange={e => setJobsSearch(e.target.value)}
                  placeholder="Поиск по названию..."
                  className="px-3 py-2 rounded bg-gmod-lighter border border-gmod-lighterer text-gmod-white placeholder-gmod-white placeholder-opacity-40 focus:outline-none focus:ring-2 focus:ring-theme-primary"
                  style={{ minWidth: 200 }}
                />
                <select
                  value={jobsCategory}
                  onChange={e => setJobsCategory(e.target.value)}
                  className="px-3 py-2 rounded bg-gmod-lighter border border-gmod-lighterer text-gmod-white"
                  style={{ minWidth: 200 }}
                >
                  <option value="">Все категории</option>
                  {jobCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <button
                  className="px-3 py-2 bg-gmod-lighter text-theme-primary rounded hover:bg-theme-primary hover:text-white border border-gmod-lighterer"
                  onClick={() => { setShowJobs(false); /* jobs не очищаем */ setJobsSearch(''); setJobsCategory(''); }}
                >Скрыть</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredJobs.map(([id, job]) => {console.log(`https://yufu.su/spawnicons/${job.model[0].slice(0, -4)}.png`); return (
              <div
                key={id}
                className="flex flex-col gap-2 bg-gmod-lighter/80 rounded-xl p-6 shadow cursor-pointer border-2 border-gmod-lighterer hover:border-theme-primary transition"
                onClick={() => handleSetWhitelist(id)}
              >
                <div className="flex items-center gap-4">
                  {job.model && Array.isArray(job.model) && job.model[0] && typeof job.model[0] === 'string' && (
                    <img
                      src={`https://yufu.su/spawnicons/${job.model[0].slice(0, -4)}.png`}
                      alt="icon"
                      className="w-20 h-20 rounded bg-gmod-dark border border-gmod-lighterer"
                      onError={(e) => {
                        console.error('Image failed to load:', e);
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="flex flex-col gap-1">
                    <span
                      className="text-2xl font-bold"
                      style={{
                        color:
                          job.color && job.color.r != null && job.color.g != null && job.color.b != null
                            ? `rgb(${job.color.r},${job.color.g},${job.color.b})`
                            : '#fff',
                      }}
                    >
                      {job.name || 'Без названия'}
                    </span>
                    <div className="flex gap-2 items-center mt-1">
                      <span
                        className="text-xs font-semibold px-2 py-1 rounded-full border"
                        style={{
                          border: '2px solid #DC255A', // gmod-magenta
                          color: '#DC255A',
                          background: '#181825',
                        }}
                      >
                        ID: {id}
                      </span>
                      {job.category && (
                        <span
                          className="text-xs font-semibold px-2 py-1 rounded-full border"
                          style={{
                            border: job.color && job.color.r != null && job.color.g != null && job.color.b != null
                              ? `2px solid rgb(${job.color.r},${job.color.g},${job.color.b})`
                              : '2px solid #a1a1aa',
                            color: job.color && job.color.r != null && job.color.g != null && job.color.b != null
                              ? `rgb(${job.color.r},${job.color.g},${job.color.b})`
                              : '#fff',
                            background: '#181825',
                          }}
                        >
                          {job.category}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )})}
                {filteredJobs.length === 0 && (
                  <div className="col-span-2 text-gmod-white opacity-60 text-center py-8">Нет профессий по фильтру</div>
                )}
              </div>
            </>
          ) : (user?.rank === 'superadmin' || user?.rank === 'yufuadmin' || user?.rank === 'curator' || user?.rank === 'seniorstaff') ? (
            <div className="flex flex-col items-center gap-4">
              <button
                className="px-6 py-2 bg-theme-primary text-white rounded-lg font-bold text-lg hover:bg-theme-primary-hover transition"
                onClick={fetchJobsCache}
                disabled={jobsLoading}
              >{jobsLoading ? 'Запрос...' : 'Запросить'}</button>
            </div>
          ) : (
            <div className="text-gmod-white opacity-60 text-center py-8">Профессии ещё не загружены</div>
          )}
          {/* Модальное окно подтверждения выдачи профы */}
          {confirmJob && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
              <div className="bg-gmod-lighter rounded-2xl p-8 shadow-xl border-2 border-theme-primary flex flex-col items-center gap-4 min-w-[320px] max-w-[90vw]">
                <div className="text-xl font-bold text-theme-primary mb-2">Выдать профессию?</div>
                <div className="flex flex-col items-center gap-2">
                  <span className="text-lg font-semibold" style={{ color: confirmJob.job.color ? `rgb(${confirmJob.job.color.r},${confirmJob.job.color.g},${confirmJob.job.color.b})` : '#fff' }}>{confirmJob.job.name}</span>
                  <span className="text-xs bg-gmod-dark text-gmod-white rounded px-2 py-1">ID: {confirmJob.id}</span>
                  <span className="text-gmod-white opacity-80 text-sm">Категория: {confirmJob.job.category}</span>
                </div>
                <div className="flex gap-4 mt-4">
                  <button
                    className="px-4 py-2 bg-theme-primary text-white rounded-lg font-bold hover:bg-theme-primary-hover transition"
                    onClick={confirmSetWhitelist}
                  >Подтвердить</button>
                  <button
                    className="px-4 py-2 bg-gmod-lighter text-theme-primary rounded-lg font-bold border border-theme-primary hover:bg-theme-primary hover:text-white transition"
                    onClick={cancelSetWhitelist}
                  >Отмена</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Нижние логи */}
      <div className="mt-8 space-y-8">
        {/* Логи наказаний */}
        {player.rank !== 'superadmin' && player.rank !== 'yufuadmin' && (
          <div className="bg-gmod-dark rounded-2xl border border-gmod-lighterer p-6 shadow-lg">
            <div className="text-xl font-semibold text-theme-primary mb-2">Логи наказаний</div>
            {punLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary"></div>
              </div>
            ) : punishments.length === 0 ? (
              <div className="text-gmod-white opacity-60">Нет логов наказаний</div>
            ) : (
              <>
                <table className="min-w-full text-sm text-gmod-white opacity-80">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-center">Кто</th>
                      <th className="px-4 py-2 text-center">Тип</th>
                      <th className="px-4 py-2 text-center">Цель</th>
                      <th className="px-4 py-2 text-center">Значение</th>
                      <th className="px-4 py-2 text-center">Дата</th>
                    </tr>
                  </thead>
                  <tbody>
                    {punishments.map((log, i) => {
                      const date = log.date ? new Date(log.date * 1000) : null;
                      return (
                        <tr key={log.id + log.table + i} className="border-b border-gmod-lighterer">
                          <td className="px-4 py-2 text-center">{log.name || '-'}</td>
                          <td className="px-4 py-2 text-center">{log._type || '-'}</td>
                          <td className="px-4 py-2 text-center">{log.targetName || '-'}</td>
                          <td className="px-4 py-2 text-center">{log.value !== undefined ? String(log.value) : '-'}</td>
                          <td className="px-4 py-2 text-center">{date ? date.toLocaleString('ru-RU') : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {punTotalPages > 1 && (
                  <div className="flex justify-center space-x-2 mt-4 items-center">
                    <button
                      onClick={() => setPunCurrentPage(Math.max(1, punCurrentPage - 1))}
                      disabled={punCurrentPage === 1}
                      className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer"
                    >
                      Назад
                    </button>
                    <span className="px-2 text-gmod-white">Страница</span>
                    <input
                      type="number"
                      min={1}
                      max={punTotalPages}
                      value={punPageInput}
                      onChange={e => setPunPageInput(e.target.value.replace(/[^0-9]/g, ''))}
                      onBlur={() => {
                        let val = parseInt(punPageInput);
                        if (isNaN(val) || val < 1) val = 1;
                        if (val > punTotalPages) val = punTotalPages;
                        setPunCurrentPage(val);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          let val = parseInt(punPageInput);
                          if (isNaN(val) || val < 1) val = 1;
                          if (val > punTotalPages) val = punTotalPages;
                          setPunCurrentPage(val);
                        }
                      }}
                      className="w-14 px-2 py-1 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white text-center mx-1"
                      style={{width: 50}}
                    />
                    <span className="px-2 text-gmod-white">из {punTotalPages}</span>
                    <button
                      onClick={() => setPunCurrentPage(Math.min(punTotalPages, punCurrentPage + 1))}
                      disabled={punCurrentPage === punTotalPages}
                      className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer"
                    >
                      Вперёд
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {/* --- PVP-логи --- */}
        <div className="bg-gmod-dark rounded-2xl border border-gmod-lighterer p-6 shadow-lg">
          <div className="text-xl font-semibold text-theme-primary mb-2">PVP-логи</div>
          {pvpLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary"></div>
            </div>
          ) : pvpLogs.length === 0 ? (
            <div className="text-gmod-white opacity-60">Нет PVP-логов</div>
          ) : (
            <table className="min-w-full text-sm text-gmod-white opacity-80">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-center">Кто</th>
                  <th className="px-4 py-2 text-center">Кому</th>
                  <th className="px-4 py-2 text-center align-middle">Время</th>
                </tr>
              </thead>
              <tbody>
                {pvpLogs.map((l, i) => (
                  <tr key={i} className="border-b border-gmod-lighterer">
                    <td className="px-4 py-2 text-center">
                      {l.instigator ? (
                        <span className="inline-flex items-center">
                          {pvpAvatars[l.instigator] ? (
                            <img src={pvpAvatars[l.instigator]} alt="avatar" className="inline w-8 h-8 rounded-full mr-1 align-middle" />
                          ) : (
                            <Cat className="inline w-8 h-8 text-gmod-white opacity-40 mr-1" />
                          )}
                          <Link
                            to={`/players/${pvpSteamIds[l.instigator] || l.instigator}`}
                            className="text-theme-primary hover:underline align-middle"
                          >
                            {pvpNames[l.instigator] || l.instigator}
                          </Link>
                        </span>
                      ) : (
                        <Cat className="inline w-8 h-8 text-gmod-white opacity-40" />
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {l.victim ? (
                        <span className="inline-flex items-center">
                          {pvpAvatars[l.victim] ? (
                            <img src={pvpAvatars[l.victim]} alt="avatar" className="inline w-8 h-8 rounded-full mr-1 align-middle" />
                          ) : (
                            <Cat className="inline w-8 h-8 text-gmod-white opacity-40 mr-1" />
                          )}
                          <Link
                            to={`/players/${pvpSteamIds[l.victim] || l.victim}`}
                            className="text-theme-primary hover:underline align-middle"
                          >
                            {pvpNames[l.victim] || l.victim}
                          </Link>
                        </span>
                      ) : (
                        <Cat className="inline w-8 h-8 text-gmod-white opacity-40" />
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">{l.timestamp ? new Date(l.timestamp * 1000).toLocaleString('ru-RU') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {/* --- История скринграбов --- */}
        {screengrabLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary"></div>
          </div>
        ) : screengrabHistory.length > 0 && (
          <div className="bg-gmod-dark rounded-2xl border border-gmod-lighterer p-6 shadow-lg">
            <div className="text-xl font-semibold text-theme-primary mb-2 flex items-center gap-2"><Monitor className="inline w-6 h-6 mr-1"/>История скринграбов</div>
            <div className="flex flex-wrap gap-4">
              {screengrabHistory.map((grab, i) => (
                <div key={grab.key + i} className="flex flex-col items-center bg-gmod-lighter/80 rounded-xl p-3 shadow w-64">
                  <img
                    src={`data:image/jpeg;base64,${grab.image}`}
                    alt="Скриншот"
                    className="rounded mb-2 max-h-48 object-contain border border-gmod-lighterer cursor-pointer hover:opacity-90 transition"
                    onClick={() => setLightboxGrab(grab)}
                  />
                  <div className="text-xs text-gmod-white opacity-60">{grab.createdAt ? new Date(grab.createdAt).toLocaleString('ru-RU') : '-'}</div>
                </div>
              ))}
            </div>
            {screengrabTotalPages > 1 && (
              <div className="flex justify-center mt-4 gap-2">
                <button onClick={() => setScreengrabPage(Math.max(1, screengrabPage - 1))} disabled={screengrabPage === 1} className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50">Назад</button>
                <span className="px-2 text-gmod-white">Страница {screengrabPage} из {screengrabTotalPages}</span>
                <button onClick={() => setScreengrabPage(Math.min(screengrabTotalPages, screengrabPage + 1))} disabled={screengrabPage === screengrabTotalPages} className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50">Вперёд</button>
              </div>
            )}
          </div>
        )}
        {/* --- Команды сайта --- */}
        <div className="bg-gmod-dark rounded-2xl border border-gmod-lighterer p-6 shadow-lg">
          <div className="text-xl font-semibold text-theme-primary mb-2">Команды сайта</div>
          {commands.length === 0 ? <div className="text-gmod-white opacity-60">Нет команд сайта</div> : (
            <>
              <table className="min-w-full text-sm text-gmod-white opacity-80">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-center">Команда</th>
                    <th className="px-4 py-2 text-center">Причина</th>
                    <th className="px-4 py-2 text-center">Длительность</th>
                    <th className="px-4 py-2 text-center">Кем</th>
                    <th className="px-4 py-2 text-center">Время</th>
                    <th className="px-4 py-2 text-center">Результат</th>
                  </tr>
                </thead>
                <tbody>
                  {commands.map((c, i) => {
                    let commandDisplay = c.command;
                    if (typeof c.command === 'string' && c.command.startsWith('screengrab')) {
                      const match = c.command.match(/^screengrab\s+"([^"]+)"/);
                      if (match) {
                        commandDisplay = `screengrab, ${match[1]}`;
                      } else {
                        commandDisplay = 'screengrab';
                      }
                    }
                    return (
                      <tr key={i} className="border-b border-gmod-lighterer">
                        <td className="px-4 py-2 text-center">{commandDisplay}</td>
                        <td className="px-4 py-2 text-center">{c.reason || '-'}</td>
                        <td className="px-4 py-2 text-center">{c.duration === 0 ? 'Навсегда' : c.duration || '-'}</td>
                        <td className="px-4 py-2 text-center">{c.executedBy?.displayName || c.executedBy?.steamId || '-'}</td>
                        <td className="px-4 py-2 text-center">{c.executedAt ? new Date(c.executedAt).toLocaleString('ru-RU') : '-'}</td>
                        <td className="px-4 py-2 text-center">{c.result || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {cmdTotalPages > 1 && (
                <div className="flex justify-center space-x-2 mt-4 items-center">
                  <button onClick={() => setCmdPage(Math.max(1, cmdPage - 1))} disabled={cmdPage === 1} className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50">Назад</button>
                  <span className="px-2 text-gmod-white">Страница</span>
                  <input
                    type="number"
                    min={1}
                    max={cmdTotalPages}
                    value={cmdPageInput}
                    onChange={e => setCmdPageInput(e.target.value.replace(/[^0-9]/g, ''))}
                    onBlur={() => {
                      let val = parseInt(cmdPageInput);
                      if (isNaN(val) || val < 1) val = 1;
                      if (val > cmdTotalPages) val = cmdTotalPages;
                      setCmdPage(val);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        let val = parseInt(cmdPageInput);
                        if (isNaN(val) || val < 1) val = 1;
                        if (val > cmdTotalPages) val = cmdTotalPages;
                        setCmdPage(val);
                      }
                    }}
                    className="w-14 px-2 py-1 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white text-center mx-1"
                    style={{width: 50}}
                  />
                  <span className="px-2 text-gmod-white">из {cmdTotalPages}</span>
                  <button onClick={() => setCmdPage(Math.min(cmdTotalPages, cmdPage + 1))} disabled={cmdPage === cmdTotalPages} className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50">Вперёд</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {lightboxGrab && (
        <ScreengrabLightbox
          image={lightboxGrab.image}
          subtitle={lightboxGrab.createdAt ? new Date(lightboxGrab.createdAt).toLocaleString('ru-RU') : undefined}
          onClose={() => setLightboxGrab(null)}
        />
      )}
    </div>
  );
}

export default PlayerDetails; 